if (!customElements.get('classic-slideshow-component')) {
  class ClassicSlideshowComponent extends HTMLElement {
    constructor() {
      super();

      this.currentPage = 1;
      this.totalPages = 1;
      this.pagePositions = [0];
      this.pageButtons = [];
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.autoplay = null;
      this.autoplayButtonIsSetToPlay = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPreviousClick = this.onPreviousClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onPageControlClick = this.onPageControlClick.bind(this);
      this.onAutoplayToggle = this.onAutoplayToggle.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    }

    connectedCallback() {
      this.teardown();
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
    }

    setup() {
      this.slider = this.querySelector('[id^="Slider-"]');
      this.slides = Array.from(this.querySelectorAll('[id^="Slide-"]'));
      this.controls = Array.from(this.querySelectorAll('.slideshow__controls'));
      this.prevButtons = Array.from(this.querySelectorAll('button[name="previous"]'));
      this.nextButtons = Array.from(this.querySelectorAll('button[name="next"]'));
      this.counterCurrentElements = Array.from(this.querySelectorAll('.slider-counter--current'));
      this.counterTotalElements = Array.from(this.querySelectorAll('.slider-counter--total'));
      this.pageControlWrappers = Array.from(this.querySelectorAll('[data-slider-page-controls]'));
      this.autoplayButtons = Array.from(this.querySelectorAll('.slideshow__autoplay'));
      this.controlStyle = this.dataset.controlStyle || 'counter';
      this.slideLabel = this.dataset.slideLabel || 'Load slide';
      this.ofLabel = this.dataset.ofLabel || 'of';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoRotate = this.slider?.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(this.slider?.dataset.speed || 5) * 1000;
      this.autoplayButtonIsSetToPlay = this.autoRotate;
      this.pauseLabel =
        this.autoplayButtons[0]?.getAttribute('aria-label') ||
        window.accessibilityStrings?.pauseSlideshow ||
        'Pause slideshow';
      this.playLabel = window.accessibilityStrings?.playSlideshow || 'Play slideshow';

      if (!this.slider || !this.slides.length) return;

      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButtons.forEach((button) => button.addEventListener('click', this.onPreviousClick));
      this.nextButtons.forEach((button) => button.addEventListener('click', this.onNextClick));
      this.autoplayButtons.forEach((button) => button.addEventListener('click', this.onAutoplayToggle));

      if (this.autoRotate) {
        this.addEventListener('mouseenter', this.onPointerEnter);
        this.addEventListener('mouseleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);

        if (this.reducedMotion?.addEventListener) {
          this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
        } else if (this.reducedMotion?.addListener) {
          this.reducedMotion.addListener(this.onReducedMotionChange);
        }
      }

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.slider);
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));

      this.calculatePages();
      this.syncAutoplayButtons();
    }

    teardown() {
      this.pause();
      this.clearSliderHeight();

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.slider) {
        this.slider.removeEventListener('scroll', this.onScroll);
      }

      if (this.prevButtons) {
        this.prevButtons.forEach((button) => button.removeEventListener('click', this.onPreviousClick));
      }

      if (this.nextButtons) {
        this.nextButtons.forEach((button) => button.removeEventListener('click', this.onNextClick));
      }

      this.unbindPageButtons();

      if (this.autoplayButtons) {
        this.autoplayButtons.forEach((button) => button.removeEventListener('click', this.onAutoplayToggle));
      }

      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);

      if (this.reducedMotion?.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      } else if (this.reducedMotion?.removeListener) {
        this.reducedMotion.removeListener(this.onReducedMotionChange);
      }
    }

    calculatePages() {
      if (!this.slider || !this.slides.length) return;

      const visibleSlides = this.slides.filter((slide) => slide.clientWidth > 0);
      if (!visibleSlides.length) return;

      const lastVisibleSlide = visibleSlides[visibleSlides.length - 1];
      const maxScrollLeft = Math.max(
        0,
        lastVisibleSlide.offsetLeft + lastVisibleSlide.clientWidth - this.slider.clientWidth
      );
      const nextPagePositions = [];

      visibleSlides.forEach((slide) => {
        const position = Math.min(slide.offsetLeft, maxScrollLeft);
        if (!nextPagePositions.length || Math.abs(nextPagePositions[nextPagePositions.length - 1] - position) > 1) {
          nextPagePositions.push(position);
        }
      });

      this.pagePositions = nextPagePositions.length ? nextPagePositions : [0];
      this.totalPages = this.pagePositions.length;

      this.classList.toggle('classic-slideshow--static', this.totalPages <= 1);

      this.renderPageControls();
      this.updateControlsHeight();
      this.syncFromScrollPosition();

      if (this.totalPages <= 1) {
        this.pause();
      } else if (this.autoRotate && this.autoplayButtonIsSetToPlay && !this.reducedMotion.matches) {
        this.play();
      }
    }

    renderPageControls() {
      this.unbindPageButtons();

      if (this.controlStyle === 'counter' || !this.pageControlWrappers.length) {
        this.pageButtons = [];
        return;
      }

      this.pageControlWrappers.forEach((wrapper) => {
        wrapper.innerHTML = '';
        const fragment = document.createDocumentFragment();

        for (let index = 0; index < this.totalPages; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `slider-counter__link slider-counter__link--${this.controlStyle} link`;
          button.dataset.pageIndex = String(index);
          button.setAttribute('aria-controls', this.slider.id);
          button.setAttribute('aria-label', `${this.slideLabel} ${index + 1} ${this.ofLabel} ${this.totalPages}`);

          if (this.controlStyle === 'numbers') {
            button.textContent = String(index + 1);
          } else {
            const dot = document.createElement('span');
            dot.className = 'dot';
            button.appendChild(dot);
          }

          fragment.appendChild(button);
        }

        wrapper.appendChild(fragment);
      });

      this.pageButtons = Array.from(this.querySelectorAll('[data-slider-page-controls] .slider-counter__link'));
      this.pageButtons.forEach((button) => button.addEventListener('click', this.onPageControlClick));
    }

    unbindPageButtons() {
      if (!this.pageButtons?.length) return;
      this.pageButtons.forEach((button) => button.removeEventListener('click', this.onPageControlClick));
      this.pageButtons = [];
    }

    onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncFromScrollPosition();
      });
    }

    onResize() {
      if (this.resizeFrame) return;

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.calculatePages();
      });
    }

    syncFromScrollPosition() {
      if (!this.slider) return;

      this.currentPage = this.findNearestPageIndex(this.slider.scrollLeft) + 1;
      this.updateCounters();
      this.updatePageButtons();
      this.updateArrowButtons();
      this.setSlideVisibility();
      this.updateAdaptiveRatios();
      this.updateSliderHeight();
    }

    findNearestPageIndex(scrollLeft) {
      if (!this.pagePositions.length) return 0;

      return this.pagePositions.reduce((closestIndex, position, index, positions) => {
        const currentDistance = Math.abs(position - scrollLeft);
        const closestDistance = Math.abs(positions[closestIndex] - scrollLeft);
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);
    }

    updateCounters() {
      this.counterCurrentElements.forEach((element) => {
        element.textContent = String(this.currentPage);
      });

      this.counterTotalElements.forEach((element) => {
        element.textContent = String(this.totalPages);
      });
    }

    updatePageButtons() {
      this.pageButtons.forEach((button) => {
        const isActive = Number(button.dataset.pageIndex) === this.currentPage - 1;
        button.classList.toggle('slider-counter__link--active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    updateArrowButtons() {
      const disableButtons = this.totalPages <= 1;
      [...this.prevButtons, ...this.nextButtons].forEach((button) => {
        button.toggleAttribute('disabled', disableButtons);
      });
    }

    onPreviousClick(event) {
      event.preventDefault();
      this.stepToPage(-1);
    }

    onNextClick(event) {
      event.preventDefault();
      this.stepToPage(1);
    }

    onPageControlClick(event) {
      event.preventDefault();
      const pageIndex = Number(event.currentTarget.dataset.pageIndex || 0);
      this.scrollToPage(pageIndex);
      this.restartAutoplay();
    }

    stepToPage(direction) {
      if (this.totalPages <= 1) return;

      let nextPageIndex = this.currentPage - 1 + direction;
      let behavior = this.reducedMotion.matches ? 'auto' : 'smooth';

      if (nextPageIndex < 0) {
        nextPageIndex = this.totalPages - 1;
        behavior = 'auto';
      }

      if (nextPageIndex >= this.totalPages) {
        nextPageIndex = 0;
        behavior = 'auto';
      }

      this.scrollToPage(nextPageIndex, behavior);
      this.restartAutoplay();
    }

    scrollToPage(pageIndex, behavior = this.reducedMotion.matches ? 'auto' : 'smooth') {
      if (!this.slider || !this.pagePositions.length) return;

      const safePageIndex = Math.max(0, Math.min(pageIndex, this.pagePositions.length - 1));
      if (behavior === 'auto') {
        this.slider.scrollLeft = this.pagePositions[safePageIndex];
        return;
      }

      this.slider.scrollTo({
        left: this.pagePositions[safePageIndex],
        behavior,
      });
    }

    scrollToSlide(slide) {
      if (!slide || !this.pagePositions.length) return;

      let pageIndex = 0;
      this.pagePositions.forEach((position, index) => {
        if (position <= slide.offsetLeft + 1) {
          pageIndex = index;
        }
      });

      this.scrollToPage(pageIndex, 'auto');
    }

    setSlideVisibility() {
      if (!this.slider) return;

      const viewportStart = this.slider.scrollLeft - 1;
      const viewportEnd = this.slider.scrollLeft + this.slider.clientWidth + 1;

      this.slides.forEach((slide) => {
        const slideStart = slide.offsetLeft;
        const slideEnd = slideStart + slide.clientWidth;
        const isVisible = slideStart >= viewportStart && slideEnd <= viewportEnd;

        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        if (isVisible) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }

        this.toggleSlideFocus(slide, isVisible);
        if (isVisible) {
          this.playSlideVideos(slide);
        } else {
          this.pauseSlideVideos(slide);
        }
      });
    }

    getVisibleSlides() {
      if (!this.slider) return [];

      const viewportStart = this.slider.scrollLeft - 1;
      const viewportEnd = this.slider.scrollLeft + this.slider.clientWidth + 1;

      return this.slides.filter((slide) => {
        if (slide.clientWidth <= 0) return false;
        const slideStart = slide.offsetLeft;
        const slideEnd = slideStart + slide.clientWidth;
        return slideStart >= viewportStart && slideEnd <= viewportEnd;
      });
    }

    getCurrentPageSlide() {
      if (!this.slider || !this.slides.length) return null;

      const safePageIndex = Math.max(0, Math.min(this.currentPage - 1, this.pagePositions.length - 1));
      const pagePosition = this.pagePositions[safePageIndex] ?? this.slider.scrollLeft;
      const tolerance = 1;

      return (
        this.slides.find((slide) => {
          if (slide.clientWidth <= 0) return false;

          const slideStart = slide.offsetLeft;
          const slideEnd = slideStart + slide.clientWidth;
          return slideStart <= pagePosition + tolerance && slideEnd >= pagePosition + tolerance;
        }) || null
      );
    }

    updateAdaptiveRatios() {
      const visibleSlides = this.getVisibleSlides();
      if (!visibleSlides.length) return;

      const updateRatio = (variableName, datasetKey) => {
        const ratios = visibleSlides
          .map((slide) => Number.parseFloat(slide.dataset[datasetKey] || ''))
          .filter((ratio) => Number.isFinite(ratio) && ratio > 0);

        if (!ratios.length) return;
        this.style.setProperty(variableName, String(Math.min(...ratios)));
      };

      updateRatio('--classic-slideshow-ratio-mobile', 'ratioMobile');
      updateRatio('--classic-slideshow-ratio-tablet', 'ratioTablet');
      updateRatio('--classic-slideshow-ratio-desktop', 'ratioDesktop');
    }

    updateControlsHeight() {
      const visibleControls =
        this.totalPages > 1
          ? this.controls.find((controls) => window.getComputedStyle(controls).display !== 'none')
          : null;
      const controlsHeight = visibleControls ? Math.ceil(visibleControls.getBoundingClientRect().height) : 0;
      this.style.setProperty('--classic-slideshow-controls-height', `${controlsHeight}px`);
    }

    updateSliderHeight() {
      if (!this.slider) return;

      if (!this.isMixedMobileBottomMode()) {
        this.clearSliderHeight();
        return;
      }

      const targetSlide = this.getCurrentPageSlide() || this.getVisibleSlides()[0];
      const targetHeight = targetSlide ? Math.ceil(targetSlide.getBoundingClientRect().height) : 0;

      if (targetHeight > 0) {
        this.slider.style.setProperty('height', `${targetHeight}px`);
      } else {
        this.clearSliderHeight();
      }
    }

    clearSliderHeight() {
      if (!this.slider) return;
      this.slider.style.removeProperty('height');
    }

    isMixedMobileBottomMode() {
      return this.classList.contains('classic-slideshow--mixed-mobile-bottom') && window.innerWidth < 750;
    }

    toggleSlideFocus(slide, isVisible) {
      slide
        .querySelectorAll(
          'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls], [tabindex]'
        )
        .forEach((element) => {
          if (!element.hasAttribute('data-slider-original-tabindex')) {
            element.setAttribute('data-slider-original-tabindex', element.getAttribute('tabindex') || '');
          }

          const originalTabIndex = element.getAttribute('data-slider-original-tabindex');

          if (isVisible) {
            if (originalTabIndex === '') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabIndex);
            }
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });
    }

    pauseSlideVideos(slide) {
      slide.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    }

    playSlideVideos(slide) {
      slide.querySelectorAll('video').forEach((video) => {
        if (!video.autoplay) return;

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    onAutoplayToggle() {
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
      this.syncAutoplayButtons();

      if (this.autoplayButtonIsSetToPlay) {
        this.play();
      } else {
        this.pause();
      }
    }

    onPointerEnter() {
      if (this.autoplayButtonIsSetToPlay) this.pause();
    }

    onPointerLeave() {
      if (this.autoplayButtonIsSetToPlay) this.play();
    }

    onFocusIn(event) {
      if (!this.autoplayButtonIsSetToPlay) return;

      const focusedOnAutoplayButton = this.autoplayButtons.some(
        (button) => button === event.target || button.contains(event.target)
      );

      if (!focusedOnAutoplayButton) {
        this.pause();
      }
    }

    onFocusOut(event) {
      if (!this.autoplayButtonIsSetToPlay) return;
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.play();
    }

    onReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.pause();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.play();
      }
    }

    syncAutoplayButtons() {
      this.autoplayButtons.forEach((button) => {
        button.classList.toggle('slideshow__autoplay--paused', !this.autoplayButtonIsSetToPlay);
        button.setAttribute('aria-label', this.autoplayButtonIsSetToPlay ? this.pauseLabel : this.playLabel);
      });
    }

    restartAutoplay() {
      if (!this.autoRotate || !this.autoplayButtonIsSetToPlay) return;
      this.pause();
      this.play();
    }

    autoRotateSlides() {
      if (this.totalPages <= 1) return;
      this.stepToPage(1);
    }

    play() {
      if (!this.autoRotate || !this.slider || this.totalPages <= 1 || this.reducedMotion.matches) return;

      this.slider.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(() => this.autoRotateSlides(), this.autoplaySpeed);
    }

    pause() {
      if (this.slider) {
        this.slider.setAttribute('aria-live', 'polite');
      }

      clearInterval(this.autoplay);
      this.autoplay = null;
    }
  }

  customElements.define('classic-slideshow-component', ClassicSlideshowComponent);
}
